import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, RefreshCw, UsersRound } from "lucide-react";
import { useUsers } from "@/hooks/useUsers";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { fullName } from "@/lib/format";
import { L } from "@/i18n";
import { lastSeenOf, usagePctOf } from "@/lib/usage";
import { UsersToolbar } from "@/components/users/UsersToolbar";
import { UsersTable } from "@/components/users/UsersTable";
import { UserConfirm } from "@/components/users/UserConfirm";
import { Pager } from "@/components/users/Pager";

const PAGE_SIZE = 25;

const ts = (v) => (v ? new Date(/T[\d:.]+$/.test(v) ? `${v}Z` : v).getTime() || -Infinity : -Infinity);
const SORT_VALUE = {
  last_seen: (u) => ts(lastSeenOf(u)),
  usage: (u) => usagePctOf(u) ?? -1,
  created: (u) => ts(u.created_at),
};
const FIRST_DIR = { last_seen: "asc", usage: "asc", created: "desc" };

const matches = (u, q) =>
  !q || [fullName(u), u.username, u.email].some((v) => v?.toLowerCase().includes(q));

const Empty = ({ icon: Icon, text, children }) => (
  <div className="py-16 text-center" data-testid="users-empty">
    <Icon className="mx-auto h-6 w-6 text-slate-600" />
    <p className="mt-3 text-sm text-slate-400">{text}</p>
    {children}
  </div>
);

export default function Users() {
  const { user: me } = useAuth();
  const { data: users = [], isLoading, error, refetch, isFetching } = useUsers();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [role, setRole] = useState("all");
  const [page, setPage] = useState(1);
  const [request, setRequest] = useState(null);
  const [sort, setSort] = useState({ key: "created", dir: "desc" });

  const onSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: FIRST_DIR[key] }));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const value = SORT_VALUE[sort.key];
    const sign = sort.dir === "asc" ? 1 : -1;
    return users
      .filter((u) => matches(u, q) && (status === "all" || u.status === status) && (role === "all" || u.role === role))
      .sort((a, b) => sign * (value(a) - value(b)) || fullName(a).localeCompare(fullName(b)));
  }, [users, search, status, role, sort]);

  useEffect(() => setPage(1), [search, status, role, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const slice = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  return (
    <div data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500">{L("Management", "Gestão")}</p>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">{L("Users", "Utilizadores")}</h1>
          <p className="mt-2 text-sm text-slate-400" data-testid="users-total">{L("{n} registered accounts", "{n} contas registadas", { n: users.length })}</p>
        </div>
        <button onClick={() => refetch()} disabled={isFetching} className="self-start sm:self-auto flex items-center gap-2 rounded-md border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:border-amber-500/50 hover:text-amber-400 transition-colors disabled:opacity-60" data-testid="users-refresh-button">
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} /> {L("Refresh", "Atualizar")}
        </button>
      </div>

      <UsersToolbar search={search} onSearch={setSearch} status={status} onStatus={setStatus} role={role} onRole={setRole} />

      <section className="mt-5 rounded-lg bg-[#131f37] border border-[#1e293b] overflow-hidden">
        {isLoading && <div className="h-64 animate-pulse" data-testid="users-loading" />}
        {error && !isLoading && (
          <Empty icon={AlertTriangle} text={L("Could not load: {e}", "Não foi possível carregar: {e}", { e: errorMessage(error) })}>
            <button onClick={() => refetch()} className="mt-4 rounded-md bg-amber-500 px-4 py-2 text-xs font-semibold text-[#0a1425]" data-testid="users-retry-button">{L("Try again", "Tentar novamente")}</button>
          </Empty>
        )}
        {!isLoading && !error && (slice.length ? (
          <UsersTable users={slice} currentUserId={me?.id} onRequest={setRequest} sort={sort} onSort={onSort} />
        ) : (
          <Empty icon={UsersRound} text={L("No users match the filters.", "Nenhum utilizador corresponde aos filtros.")} />
        ))}
        {!isLoading && !error && (
          <Pager page={current} pages={pages} total={filtered.length} from={(current - 1) * PAGE_SIZE + 1} to={Math.min(current * PAGE_SIZE, filtered.length)} onPage={setPage} />
        )}
      </section>

      <UserConfirm request={request} onClose={() => setRequest(null)} />
    </div>
  );
}
